import * as joint from "@clientio/rappid";
import { Transition } from "../mvc/transition";
import { State, StateView } from "../mvc/state";
import { Synchronization } from "../mvc/synchronization";
import { Region, RegionView } from "../mvc/region";
import { NodeLabel } from "../mvc/base/labeled-node";
import { Controller } from "../mvc/base/controller";
import { activeView } from "../subdiagram-viewer";

export class RappidCoreContext {
  graph: joint.dia.Graph;
  paper: joint.dia.Paper;
  controllers: Map<joint.dia.CellView, Controller> = new Map();

  constructor() {
    this.createGraph();
    this.createPaper();
  }

  private createGraph() {
    this.graph = new joint.dia.Graph({}, { cellNamespace: joint.shapes });
  }

  private createPaper() {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const context = this;
    // @ts-expect-error - JointJS typings are outdated: createViewForModel actually accepts (cell, cid)
    this.paper = new (class extends joint.dia.Paper {
      constructor() {
        super({
          model: context.graph,
          width: 1024,
          height: 1024,
          gridSize: -1,
          drawGrid: false,
          interactive: { labelMove: true },
          async: true,
          sorting: joint.dia.Paper.sorting.APPROX,
          embeddingMode: true,
          moveThreshold: 4,
          cellViewNamespace: joint.shapes,
          linkPinning: true,
          defaultLink: new Transition(),
          defaultAnchor: joint.anchors.perpendicular,
          defaultConnectionPoint: joint.connectionPoints.boundary,
          connectionStrategy: function (end, elementView, magnet, coords, link, endType) {
            if (elementView.model instanceof State || elementView.model instanceof Synchronization) {
              return context.getRelativeEndPoint(end, elementView, magnet, coords, link, endType);
            }
            if (elementView.model instanceof Region && elementView.model.getParentCell()) {
              if (elementView.model.isEmbedded() && endType === "target") {
                link.prop(["target", "id"], elementView.model.getParentCell().id);
                return context.getRelativeEndPoint(
                  link.target(),
                  elementView.model.getParentCell().findView(context.paper),
                  magnet,
                  coords,
                  link,
                  endType
                );
              } else if (elementView.model.isEmbedded() && endType === "source") {
                link.prop(["source", "id"], elementView.model.getParentCell().id);
                return context.getRelativeEndPoint(
                  link.source(),
                  elementView.model.getParentCell().findView(context.paper),
                  magnet,
                  coords,
                  link,
                  endType
                );
              }
              link.remove();
            }
            return end;
          },
          validateConnection: function (cellViewS, magnetS, cellViewT) {
            return cellViewT.model.attributes["linkable"] && cellViewS.model.attributes["linkable"];
          },
          allowLink: function (linkView) {
            const source = linkView.model.getSourceElement();
            const target = linkView.model.getTargetElement();
            if (!source || !target) {
              return false;
            }

            if (source instanceof Region && !source.getParentCell()) {
              return false;
            }
            if (target instanceof Region && !target.getParentCell()) {
              return false;
            }
            return target.attributes["linkable"] && source.attributes["linkable"];
          },
          restrictTranslate: function (elementView) {
            const element = elementView?.model;
            if (element?.get("type") !== "NodeLabel") return;
            return (element as NodeLabel).restrictTranslateToEntrySide();
          },
          validateEmbedding: function (childView, parentView) {
            const invisible = parentView.model.attr("root/display") === "none";
            if (invisible) {
              return false;
            }
            const isParentRegionWithNonRegionChild =
              parentView.model instanceof Region && !(childView.model instanceof Region);
            const isParentStateWithRegionChild = parentView.model instanceof State && childView.model instanceof Region;
            const canEmbed =
              parentView.model.attributes["embedable"] &&
              (isParentRegionWithNonRegionChild || isParentStateWithRegionChild);
            return canEmbed;
          },
          validateUnembedding(childView) {
            if (childView instanceof RegionView) {
              return true;
            }
            return activeView.value === null;
          },
          frontParentOnly: false,
        });
      }

      // @ts-expect-error - JointJS typings are outdated: createViewForModel actually accepts (cell, cid)
      createViewForModel(cell: joint.dia.Cell, cid: string) {
        // @ts-expect-error - see above
        const view = super.createViewForModel(cell, cid);
        const controllerClass = this.getController(view);
        if (controllerClass) context.controllers.set(view, new controllerClass(view, context));
        return view;
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      getController(cellView: joint.dia.CellView): any {
        const type = cellView.model.get("type") + "Controller";
        return joint.util.getByPath(joint.shapes, type, ".");
      }
    })();
    context.addTargetMarker(this.paper);
  }

  private getRelativeEndPoint(
    end: joint.dia.Link.EndJSON,
    elementView: joint.dia.CellView,
    magnet: SVGElement,
    coords: joint.g.PlainPoint,
    link: joint.dia.Link<joint.dia.Link.Attributes, joint.dia.ModelSetOptions>,
    endType: joint.dia.LinkEnd
  ) {
    const relEnd = joint.connectionStrategies.pinRelative(end, elementView, magnet, coords, link, endType);
    // This will make sure that the anchor of target is calculated first
    // so the source anchor can align with target (using perpendicular anchor)
    relEnd.priority = true;
    return relEnd;
  }

  private addTargetMarker(paper: joint.dia.Paper) {
    const svgNS = "http://www.w3.org/2000/svg";

    const defs = document.createElementNS(svgNS, "defs");

    const marker = document.createElementNS(svgNS, "marker");
    marker.setAttribute("id", "targetMarker");
    marker.setAttribute("viewBox", "0 -2 4 4");
    marker.setAttribute("refX", "3");
    marker.setAttribute("refY", "0");
    marker.setAttribute("markerWidth", "3");
    marker.setAttribute("markerHeight", "4");
    marker.setAttribute("orient", "auto");

    const path = document.createElementNS(svgNS, "path");
    path.setAttribute("d", "M0,-2 L4,0 L0,2 Z");

    marker.appendChild(path);
    defs.appendChild(marker);

    paper.svg.prepend(defs);
  }
}
